// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/Dashboard.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Layers, 
  Users, 
  GraduationCap, 
  CheckSquare, 
  Building2, 
  Calendar, 
  FileText, 
  RefreshCw, 
  Download, 
  AlertCircle, 
  BookOpen, 
  Clock, 
  MapPin, 
  Activity,
  Eye,
  ChevronRight
} from 'lucide-react';
import api from '../api';
import Layout from './Layout';
import { useModuleAccess } from '../hooks/useModuleAccess';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);

  // ✅ Get tenant ID from localStorage
  const tenantId = localStorage.getItem('tenantId');

  // ✅ Use the module access hook - fetches immediately when component mounts
  const { hasModuleAccess, loading: modulesLoading } = useModuleAccess(tenantId);

  // ✅ Check if classliveview is accessible
  const hasClassLiveViewAccess = useMemo(() => {
    return hasModuleAccess('classliveview');
  }, [hasModuleAccess]);

  // ✅ Navigation schema - Only Classes, Students, and Live Monitor (conditional)
  const navItems = useMemo(() => {
    const items = [
      { path: '/head-of-institution/dashboard', label: 'Overview', icon: LayoutDashboard },
      { path: '/head-of-institution/classes', label: 'Classes', icon: Layers },
      { path: '/head-of-institution/students', label: 'Students', icon: Users },
    ];

    // ✅ Only add Live Monitor if accessible
    if (hasClassLiveViewAccess) {
      items.push({ path: '/head-of-institution/live-monitoring', label: 'Live Monitor', icon: Eye });
    }

    return items;
  }, [hasClassLiveViewAccess]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId');
      
      if (!token) {
        setError('Your login session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await api.get('/api/head-of-institution/dashboard/overview', {
        headers: {
          'x-tenant-id': tenantId
        }
      });

      if (response.data.success) {
        setDashboardData(response.data.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.message || 'Could not load your school dashboard information. Please try again.');
      setLoading(false);
    }
  }, []);

  // Fetch live monitoring summary
  const fetchLiveSummary = useCallback(async () => {
    if (!hasClassLiveViewAccess) {
      return;
    }

    try {
      setLiveLoading(true);
      const tenantId = localStorage.getItem('tenantId');
      
      if (!tenantId) return;

      const response = await api.get(`/api/head/live-monitoring?tenantId=${tenantId}`);
      
      if (response.data.success) {
        setLiveData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching live summary:', err);
    } finally {
      setLiveLoading(false);
    }
  }, [hasClassLiveViewAccess]);

  useEffect(() => {
    fetchDashboardData();
    fetchLiveSummary();

    const interval = setInterval(() => {
      fetchLiveSummary();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchDashboardData, fetchLiveSummary]);

  const handleRefresh = () => {
    fetchDashboardData();
    fetchLiveSummary();
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  const getStatIconColor = (index) => {
    const colors = [
      'text-emerald-700 bg-emerald-50 border-emerald-100', 
      'text-indigo-700 bg-indigo-50 border-indigo-100', 
      'text-amber-700 bg-amber-50 border-amber-100', 
      'text-blue-700 bg-blue-50 border-blue-100', 
      'text-purple-700 bg-purple-50 border-purple-100', 
      'text-pink-700 bg-pink-50 border-pink-100', 
      'text-teal-700 bg-teal-50 border-teal-100', 
      'text-rose-700 bg-rose-50 border-rose-100'
    ];
    return colors[index % colors.length];
  };

  // Modern Dynamic Progress Ring for Executive Readability
  const ProgressRing = ({ value }) => {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    const ringColor = numericValue >= 75 ? 'text-green-600' : numericValue >= 40 ? 'text-amber-500' : 'text-red-500';

    return (
      <div className="flex items-center gap-2">
        <span className={`text-xl font-black ${ringColor}`}>{numericValue}%</span>
        <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden hidden sm:block">
          <div className={`h-full rounded-full ${ringColor.replace('text', 'bg')}`} style={{ width: `${Math.min(numericValue, 100)}%` }} />
        </div>
      </div>
    );
  };

  // Premium Dashboard Container Shell
  const PremiumCard = ({ children, className = '', onClick }) => {
    return (
      <div 
        onClick={onClick}
        className={`
          bg-white rounded-2xl border border-gray-200 p-4 shadow-xs
          transition-all duration-200
          ${onClick ? 'cursor-pointer hover:border-indigo-300 hover:shadow-sm active:scale-[0.99] select-none' : ''}
          ${className}
        `}
      >
        {children}
      </div>
    );
  };

  // ✅ Live Class Monitoring Dashboard Card - Conditionally rendered
  const LiveMonitoringCard = useCallback(() => {
    if (!hasClassLiveViewAccess) {
      return null;
    }

    const summary = liveData?.classSummary || {};
    const liveSessions = liveData?.liveSessions || [];
    const hasLiveSessions = liveSessions.length > 0;

    return (
      <PremiumCard 
        className="border-green-200 bg-gradient-to-br from-green-50/40 via-white to-white"
        onClick={() => navigate('/head-of-institution/live-monitoring')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-xl text-green-700 border border-green-200 flex-shrink-0">
              <Eye className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">
                Live Classroom Status
              </h3>
              <p className="text-xs text-gray-400 font-medium">
                See which classes are running right now
              </p>
            </div>
          </div>
          {hasLiveSessions && (
            <span className="flex items-center gap-1 px-2.5 py-0.5 bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold rounded-full animate-pulse">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              {liveSessions.length} Active Now
            </span>
          )}
        </div>

        {liveLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-green-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quick Metrics grid summary counters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100 text-center">
                <span className="text-xl font-black text-gray-900 block leading-none">{summary.totalActiveClasses || 0}</span>
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mt-1">Total Rooms</span>
              </div>
              <div className="bg-green-50/50 p-2.5 rounded-xl border border-green-100/50 text-center">
                <span className="text-xl font-black text-green-700 block leading-none">{summary.totalLiveSessions || 0}</span>
                <span className="text-[10px] text-green-600 font-bold uppercase tracking-wider block mt-1">Live Now</span>
              </div>
              <div className="bg-blue-50/50 p-2.5 rounded-xl border border-blue-100/50 text-center">
                <span className="text-xl font-black text-blue-700 block leading-none">{summary.totalUpcomingSessions || 0}</span>
                <span className="text-[10px] text-blue-600 font-bold uppercase tracking-wider block mt-1">Up Next</span>
              </div>
              <div className="bg-amber-50/50 p-2.5 rounded-xl border border-amber-100/50 text-center">
                <span className="text-xl font-black text-amber-700 block leading-none">{summary.classesWithOverrides || 0}</span>
                <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider block mt-1">Adjusted</span>
              </div>
            </div>

            {/* List preview rows */}
            {hasLiveSessions ? (
              <div className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                {liveSessions.slice(0, 3).map((session, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-3 py-2 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                      <span className="font-bold text-gray-800 truncate">{session.className}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500 flex-shrink-0 pl-2">
                      <span className="font-medium truncate hidden sm:inline">{session.subject?.name || 'Session'}</span>
                      <span className="font-mono bg-gray-50 px-2 py-0.5 rounded border border-gray-200 font-bold text-gray-700">
                        {session.room || '—'}
                      </span>
                      {session.isOverride && (
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                          Modified
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <p className="text-xs text-gray-500 font-medium">All classrooms are currently empty or idle</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Classes will show up instantly as soon as periods begin.</p>
              </div>
            )}

            <button 
              type="button"
              onClick={() => navigate('/head-of-institution/live-monitoring')}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-1.5 shadow-xs"
            >
              <Eye className="w-3.5 h-3.5" />
              Open Live Class Tracker Screen
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </PremiumCard>
    );
  }, [hasClassLiveViewAccess, liveData, liveLoading, navigate]);

  // ✅ Show loading state while modules are being fetched
  if (modulesLoading) {
    return (
      <Layout>
        <div className="px-4 py-3 sm:py-5 max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading module access...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const DashboardContent = () => {
    if (loading) {
      return (
        <div className="space-y-4 animate-pulse">
          <div className="h-12 bg-gray-100 rounded-xl w-full" />
          <div className="h-28 bg-gray-100 rounded-2xl w-full" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}
          </div>
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div className="flex items-center justify-center min-h-[55vh] px-4">
          <PremiumCard className="p-6 max-w-sm w-full text-center">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-gray-800">No Records Loaded</h3>
            <p className="text-xs text-gray-400 mt-1">
              Your institutional statistics database sheets are currently empty.
            </p>
          </PremiumCard>
        </div>
      );
    }

    const { stats, academicYears, departmentOverview, todayAttendance, calendarEvents } = dashboardData;

    return (
      <div className="space-y-5">
        
        {/* Horizontal Navigation Menu Strip - Only Classes, Students, Live Monitor */}
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl p-1 shadow-2xs hide-scrollbar">
          <div className="flex gap-1 whitespace-nowrap">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 border ${
                    active 
                      ? 'bg-gray-900 text-white border-gray-900 shadow-xs' 
                      : 'text-gray-600 border-transparent hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-xs font-semibold text-red-700">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Header Block Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-2">
          <div>
            <h1 className="text-lg sm:text-xl font-black tracking-tight text-gray-900 uppercase">Institution Overview</h1>
            <p className="text-xs text-gray-400 font-medium">Real-time status metrics and record tracking summary</p>
          </div>
          <div className="flex gap-2">
            <button 
              type="button"
              onClick={handleRefresh} 
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
            >
              <RefreshCw className="w-3.5 h-3.5 text-gray-400" /> Refresh
            </button>
            <button 
              type="button"
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:bg-gray-800 flex items-center justify-center gap-1.5 shadow-xs active:scale-95 transition-transform"
            >
              <Download className="w-3.5 h-3.5 text-gray-300" /> Export Sheet
            </button>
          </div>
        </div>

        {/* ✅ Live Tracking Core Component - Only renders if classliveview is accessible */}
        <LiveMonitoringCard />

        {/* Main Quantitative Stats Grid Layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Students Enrolled', value: stats.totalStudents, icon: Users },
            { label: 'Active Faculty Members', value: stats.totalFaculty, icon: GraduationCap },
            { label: 'Academic Classes Mapped', value: stats.totalClasses, icon: Layers },
            { 
              label: 'Attendance Ratio Today', 
              value: todayAttendance?.overall?.attendancePercentage || 0, 
              icon: CheckSquare,
              isPercentage: true 
            },
            { 
              label: 'Configured Subjects', 
              value: departmentOverview?.reduce((sum, d) => sum + d.subjectCount, 0) || 0, 
              icon: BookOpen 
            },
            { label: 'Operating Departments', value: stats.totalDepartments, icon: Building2 },
            { label: 'Active Academic Batches', value: stats.activeAcademicYears, icon: Calendar },
            { label: 'Total Sessions Scheduled', value: todayAttendance?.overall?.sessions || 0, icon: Activity }
          ].map((item, idx) => {
            const colorMeta = getStatIconColor(idx);
            return (
              <PremiumCard key={idx} className="flex flex-col justify-between min-h-[96px]">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block leading-tight">
                    {item.label}
                  </span>
                  <div className={`p-1.5 rounded-lg border border-opacity-50 flex-shrink-0 ${colorMeta}`}>
                    <item.icon className="w-3.5 h-3.5" />
                  </div>
                </div>
                <div className="pt-2">
                  {item.isPercentage ? (
                    <ProgressRing value={item.value} />
                  ) : (
                    <span className="text-xl font-black tracking-tight text-gray-900">
                      {item.value?.toLocaleString() || 0}
                    </span>
                  )}
                </div>
              </PremiumCard>
            );
          })}
        </div>

        {/* Split Grid for Calendars and Scheduling Rows */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          
          {/* Left Block: Academic Year Timelines */}
          {academicYears && academicYears.length > 0 && (
            <PremiumCard className="space-y-3">
              <div className="flex items-center gap-1.5 pb-2 border-b border-gray-100">
                <Calendar className="w-4 h-4 text-gray-400" />
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Academic Timelines</h3>
              </div>
              
              <div className="space-y-2.5">
                {academicYears.slice(0, 2).map((ay, idx) => (
                  <div key={idx} className={`p-3 border rounded-xl space-y-2 bg-gray-50/30 ${ay.status === 'active' ? 'border-indigo-200 bg-indigo-50/10' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-bold text-gray-800">{ay.academicYearName}</h4>
                        <p className="text-[10px] text-gray-400 font-medium mt-0.5">{ay.academicYearLabel || 'Academic Track'}</p>
                      </div>
                      <span className={`px-2 py-0.2 rounded text-[9px] font-bold border uppercase tracking-wider ${
                        ay.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {ay.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 bg-white p-2 rounded-lg border border-gray-100 font-mono text-[10px]">
                      <div><span className="text-gray-400">Students:</span> <strong className="text-gray-800">{ay.totalStudents || 0}</strong></div>
                      <div><span className="text-gray-400">Classes:</span> <strong className="text-gray-800">{ay.totalClasses || 0}</strong></div>
                    </div>

                    {ay.terms && ay.terms.length > 0 && (
                      <div className="space-y-1">
                        <span className="text-[9px] text-gray-400 font-bold block uppercase tracking-wide">Academic Terms</span>
                        <div className="flex flex-wrap gap-1">
                          {ay.terms.slice(0, 3).map((term, tIdx) => (
                            <span key={tIdx} className={`px-2 py-0.5 rounded text-[10px] border font-semibold inline-flex items-center gap-1 ${
                              term.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-500 border-gray-200'
                            }`}>
                              <span className={`w-1 h-1 rounded-full ${term.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                              {term.termLabel || term.termName || `Term ${term.termNumber}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </PremiumCard>
          )}

          {/* Right Block: Scheduled Events Trackers */}
          <PremiumCard className="space-y-3">
            <div className="flex items-center gap-1.5 pb-2 border-b border-gray-100">
              <Clock className="w-4 h-4 text-gray-400" />
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Today's Schedule & Events</h3>
            </div>

            {calendarEvents && Object.keys(calendarEvents).length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                {Object.entries(calendarEvents).map(([ayId, events]) => {
                  if (!events || events.length === 0) return null;
                  const ay = academicYears?.find(a => a.academicYearId?.toString() === ayId);
                  
                  return (
                    <div key={ayId} className="space-y-2">
                      <span className="text-[9px] font-bold bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-md uppercase tracking-wider text-gray-600 block w-max">
                        {ay?.academicYearName || 'Active Track'}
                      </span>
                      <div className="space-y-2">
                        {events.slice(0, 3).map((event, eventIdx) => {
                          const badgeClasses = event.eventType === 'exam' ? 'bg-red-50 text-red-700 border-red-200' : 
                                               event.eventType === 'holiday' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                               event.eventType === 'event' ? 'bg-green-50 text-green-700 border-green-200' :
                                               'bg-blue-50 text-blue-700 border-blue-200';

                          return (
                            <div key={eventIdx} className="p-3 border border-gray-200 rounded-xl space-y-1.5 bg-white shadow-2xs">
                              <div className="flex items-start justify-between gap-3">
                                <h5 className="font-bold text-gray-800 text-xs leading-tight">{event.eventName}</h5>
                                <span className={`text-[9px] px-2 py-0.2 rounded border uppercase font-bold tracking-wider flex-shrink-0 ${badgeClasses}`}>
                                  {event.eventType || 'Activity'}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 font-semibold">
                                {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {event.location}</span>}
                                {event.startTime && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-400" /> {event.startTime} {event.endTime ? ` - ${event.endTime}` : ''}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="border border-dashed border-gray-200 bg-gray-50/50 p-6 rounded-xl text-center">
                <Calendar className="w-6 h-6 text-gray-300 mx-auto mb-1.5" />
                <p className="text-xs font-bold text-gray-600">No Operations Scheduled</p>
                <p className="text-[10px] text-gray-400 mt-0.5">There are no special calendar events or operational exams mapped for today.</p>
              </div>
            )}
          </PremiumCard>
        </div>

        {/* Secondary Info Rows Layer: Departments Mapping */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          
          <PremiumCard className="md:col-span-2 space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Department Profiles</h3>
              <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-0.5 rounded-lg border border-gray-200">
                {departmentOverview?.length || 0} Total
              </span>
            </div>

            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
              {departmentOverview && departmentOverview.length > 0 ? (
                departmentOverview.map((dept, idx) => (
                  <div key={idx} className="p-3 border border-gray-100 rounded-xl space-y-1.5 bg-white shadow-2xs">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="text-xs font-bold text-gray-800">{dept.departmentName}</span>
                      <span className="text-[10px] text-gray-400 font-bold bg-gray-50 px-2 py-0.5 rounded border border-gray-100 font-mono">
                        {dept.facultyCount} Faculty • {dept.subjectCount} Subjects
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(dept.subjects || []).slice(0, 3).map((sub, subIdx) => (
                        <span key={subIdx} className="bg-indigo-50/40 text-indigo-700 font-medium px-2 py-0.5 rounded text-[9px] border border-indigo-100/40">
                          {sub.subjectName}
                        </span>
                      ))}
                      {dept.subjects?.length > 3 && (
                        <span className="bg-gray-100 text-gray-500 font-bold px-2 py-0.5 rounded text-[9px] border border-gray-200">
                          +{dept.subjects.length - 3} More
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-400 text-center py-4 italic">No department mappings found</p>
              )}
            </div>

            <button 
              type="button"
              onClick={() => navigate('/head-of-institution/departments')}
              className="w-full py-2 bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-100 transition-colors"
            >
              Open Department Manager
            </button>
          </PremiumCard>

          {/* Grid Quick Action Buttons */}
          <div className="grid grid-cols-2 gap-2 self-start">
            {[
              { icon: Users, label: 'Students', path: '/head-of-institution/students' },
              { icon: Layers, label: 'Classes', path: '/head-of-institution/classes' },
              { icon: Calendar, label: 'Calendar', path: '/head-of-institution/calendar' },
              { icon: FileText, label: 'Reports', path: '/head-of-institution/reports' }
            ].map((action, idx) => {
              const Icon = action.icon;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleNavigate(action.path)}
                  className="p-3 bg-white border border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-center transition-all hover:border-indigo-300 hover:shadow-2xs active:scale-[0.96] group min-h-[86px]"
                >
                  <div className="p-2 rounded-xl bg-gray-50 text-gray-500 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[10px] font-bold text-gray-700 group-hover:text-gray-900 transition-colors leading-tight">
                    {action.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

      </div>
    );
  };

  return (
    <Layout>
      <div className="px-4 py-3 sm:py-5 max-w-7xl mx-auto">
        <DashboardContent />
      </div>
    </Layout>
  );
};

export default Dashboard;